import Phaser from 'phaser';
import './style.css';
import { BootScene } from './scenes/BootScene';
import { GameScene, defaultRunData } from './scenes/GameScene';
import { MenuScene } from './scenes/MenuScene';
import { saveStore } from './services';
import { LEVELS } from './data/levels';
import { ARENA_LAYOUT, getArenaBounds, type ArenaBounds } from './render/ProceduralRenderer';
import {
  applyRenderScale,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  getCameraWorldViewport,
  RENDER_SCALE,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from './render/render-scale';
import { roomLeft, ROOM_WIDTH } from './data/world';
import type { Direction, EnemyKind, FruitBehavior, FruitKind, FruitPhase, LaneSprintPhase } from './sim/types';

const query = new URLSearchParams(window.location.search);
document.body.classList.toggle('force-touch', query.get('touch') === '1');
document.body.classList.toggle('reduced-motion', saveStore.data.settings.reducedMotion);
document.body.classList.toggle('high-contrast', saveStore.data.settings.highContrast);

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game',
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
  backgroundColor: '#050817',
  transparent: false,
  antialias: true,
  render: {
    antialias: true,
    roundPixels: false,
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
  },
  scene: [BootScene, MenuScene, GameScene],
  banner: false,
});

export interface IcecatDebugApi {
  startLevel(levelId: number): void;
  completeObjectives(): void;
  completeFruitWave(): void;
  triggerShift(): void;
  toggleInvulnerable(): void;
  winLevel(): void;
  thawPlayer(): void;
  /** Visual QA helper: magnify the arena around a grid cell (0 resets). */
  inspect(zoom: number, cellX?: number, cellY?: number): void;
  snapshot(): {
    level: number | null;
    /** Fixed-step counter, used by tests to verify the 60 Hz cadence. */
    tick: number;
    state: string;
    cores: number;
    walls: number;
    wallCells: number;
    /** Every occupied Frostwall cell, so QA can navigate around real ice. */
    wallCellList: readonly { x: number; y: number }[];
    meltingCells: number;
    phaserTextCount: number;
    arena: ArenaBounds | null;
    /** Room of the connected world the cat is standing in, zero based. */
    room: number;
    /** Rooms retained by the renderer for a settled view or doorway pan. */
    presentedRooms: readonly number[];
    roomTransitioning: boolean;
    cameraCenterX: number;
    /** World-space size of the game canvas, independent of backing resolution. */
    viewport: { width: number; height: number; renderScale: number };
    savedUnlocked: number;
    players: readonly { x: number; y: number }[];
    enemies: readonly {
      kind: EnemyKind;
      x: number;
      y: number;
      facing: Direction;
      /** Fixed steps per tile right now, so QA can prove a charge is faster. */
      stepTicks: number;
      sprintPhase: LaneSprintPhase | null;
      sprintTicks: number;
      stunned: boolean;
    }[];
    fruits: readonly {
      kind: FruitKind;
      behavior: FruitBehavior;
      phase: FruitPhase;
      collectible: boolean;
      x: number;
      y: number;
    }[];
  };
}

declare global {
  interface Window {
    __ICECAT_DEBUG__?: IcecatDebugApi;
    /** @deprecated Preserved for earlier smoke harnesses. */
    __RIFT_DEBUG__?: IcecatDebugApi;
  }
}

/** Bounds of the room on screen, in camera space. */
function visibleArenaBounds(scene: GameScene): ArenaBounds {
  const level = scene.simulation.level;
  const roomWide = Math.min(level.width, ROOM_WIDTH);
  const bounds = getArenaBounds(roomWide, level.height);
  if (level.width <= roomWide) return bounds;
  const camera = scene.cameras.main;
  const viewport = getCameraWorldViewport(camera);
  const room = scene.simulation.roomOf(scene.simulation.players[0]?.cell ?? { x: 0, y: 0 });
  const offset = roomLeft(room) * ARENA_LAYOUT.tile - viewport.x;
  return {
    ...bounds,
    x: bounds.x + offset,
    right: bounds.right + offset,
    outerX: bounds.outerX + offset,
    outerRight: bounds.outerRight + offset,
  };
}

if (query.get('debug') === '1') {
  const debugApi: IcecatDebugApi = {
  startLevel(levelId) {
    game.scene.start('GameScene', defaultRunData(Math.max(1, Math.min(LEVELS.length, levelId))));
  },
  completeObjectives() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    scene?.debugCompleteObjectives();
  },
  completeFruitWave() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    scene?.debugCompleteFruitWave();
  },
  triggerShift() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    scene?.debugShift();
  },
  toggleInvulnerable() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    scene?.debugToggleInvulnerable();
  },
  winLevel() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    scene?.debugWin();
  },
  thawPlayer() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    scene?.debugThaw();
  },
  inspect(zoom, cellX, cellY) {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    if (!scene) return;
    const camera = scene.cameras.main;
    if (zoom <= 0) {
      applyRenderScale(camera);
      return;
    }
    applyRenderScale(camera, zoom);
    if (cellX === undefined || cellY === undefined) return;
    camera.centerOn(
      ARENA_LAYOUT.x + cellX * ARENA_LAYOUT.tile + ARENA_LAYOUT.tile / 2,
      ARENA_LAYOUT.y + cellY * ARENA_LAYOUT.tile + ARENA_LAYOUT.tile / 2,
    );
  },
  snapshot() {
    const scene = game.scene.getScene('GameScene') as unknown as GameScene;
    const cameraViewport = scene ? getCameraWorldViewport(scene.cameras.main) : null;
    return {
      level: scene?.simulation?.definition.id ?? null,
      tick: scene?.simulation?.tickCount ?? 0,
      state: scene?.simulation?.worldState ?? 'menu',
      cores: scene?.simulation?.coresRemaining.size ?? 0,
      walls: scene?.simulation?.flipwalls.lines.length ?? 0,
      wallCells: scene?.simulation?.flipwalls.lines.reduce((total, line) => total + line.cells.length, 0) ?? 0,
      wallCellList: scene?.simulation?.flipwalls.lines.flatMap((line) => line.cells.map((cell) => ({ x: cell.x, y: cell.y }))) ?? [],
      meltingCells: scene?.simulation?.meltingCells.size ?? 0,
      phaserTextCount: scene?.children?.list.filter((child) => child instanceof Phaser.GameObjects.Text).length ?? 0,
      // The connected world is far wider than the screen, so the play area a
      // HUD panel could actually cover is the room currently in view, shifted
      // into camera space.
      arena: scene?.simulation ? visibleArenaBounds(scene) : null,
      room: scene?.simulation ? scene.simulation.roomOf(scene.simulation.players[0]?.cell ?? { x: 0, y: 0 }) : 0,
      presentedRooms: scene?.debugPresentedRooms() ?? [],
      roomTransitioning: scene?.debugRoomTransitioning() ?? false,
      cameraCenterX: cameraViewport?.centerX ?? 0,
      viewport: { width: WORLD_WIDTH, height: WORLD_HEIGHT, renderScale: RENDER_SCALE },
      savedUnlocked: saveStore.data.unlockedLevel,
      players: scene?.simulation?.players.map((player) => ({ x: player.cell.x, y: player.cell.y })) ?? [],
      enemies: scene?.simulation?.enemies.map((enemy) => ({
        kind: enemy.kind,
        x: enemy.cell.x,
        y: enemy.cell.y,
        facing: enemy.facing,
        stepTicks: scene.simulation.enemyStepTicks(enemy),
        sprintPhase: enemy.sprint?.phase ?? null,
        sprintTicks: enemy.sprint?.ticksRemaining ?? 0,
        stunned: enemy.stunnedTicks > 0,
      })) ?? [],
      fruits: scene?.simulation?.getActiveFruitActors().map((fruit) => ({
        kind: fruit.kind,
        behavior: fruit.behavior,
        phase: fruit.phase,
        collectible: fruit.collectible,
        x: fruit.cell.x,
        y: fruit.cell.y,
      })) ?? [],
    };
  },
  };
  window.__ICECAT_DEBUG__ = debugApi;
  window.__RIFT_DEBUG__ = debugApi;
}
